import { TreeProjectRowSkeleton } from "@/components/skeletons";
import { TreeProjectRow } from "@/components/teacher/score/TreeProjectRow";
import type { ClassroomLessonTreeQuery } from "@/generated/graphql";
import { format, useT } from "@/i18n";
import { HiChevronLeft } from "react-icons/hi";

type TreeLevel = ClassroomLessonTreeQuery["classroomLessonTree"];
type TreeLesson = TreeLevel["lessons"][number];

/** A folder's lessons, as the table reads them. */
export interface LessonGroup {
  name: string;
  lessons: TreeLesson[];
}

interface ClassLessonTreeCardProps {
  level: TreeLevel | undefined;
  /** The folders walked into, outermost first — drives the crumb trail. */
  path: readonly { projectId: string; name: string }[];
  onEnterProject: (project: { projectId: string; name: string }) => void;
  onBack: () => void;
  onOpenGroup: (group: LessonGroup) => void;
  loading?: boolean;
  /** A cold failure for this level — distinct from a level that is genuinely empty. */
  failed?: boolean;
}

const CRUMB_JOIN = " › ";
const SKELETON_ROWS = ["a", "b", "c", "d"];

/**
 * The class's content as its students browse it, down to the folder that holds lessons: that one
 * opens the class's table. Lessons are not listed — they are the table's columns, and a row of
 * them here would only be a second way in.
 */
export function ClassLessonTreeCard({
  level,
  path,
  onEnterProject,
  onBack,
  onOpenGroup,
  loading,
  failed,
}: ClassLessonTreeCardProps) {
  const t = useT();
  const navigable = level?.projects.filter((project) => project.holdsProjects) ?? [];
  const lessonFolders = level?.projects.filter((project) => !project.holdsProjects) ?? [];
  const looseLessons = level?.lessons ?? [];
  const isEmpty = navigable.length === 0 && lessonFolders.length === 0 && looseLessons.length === 0;

  const lessonCount = (count: number) =>
    format(t[count === 1 ? "report.lessons.lessonCountOne" : "report.lessons.lessonCountOther"], {
      count,
    });

  return (
    <div className="app-card px-5 py-4">
      <p className="text-[11px] font-extrabold uppercase tracking-wider text-app-muted">
        {t["selfStudy.lessons.title"]}
      </p>

      {path.length > 0 && (
        <button
          type="button"
          onClick={onBack}
          aria-label={t["report.lessons.back"]}
          className="mt-2 flex w-full items-center gap-1 border-gray-100 border-b pb-2 text-left"
        >
          <HiChevronLeft className="h-4 w-4 shrink-0 text-app-ink" />
          <span className="flex-1 truncate text-[12px] font-bold text-app-ink">
            {path.map((node) => node.name).join(CRUMB_JOIN)}
          </span>
        </button>
      )}

      {loading ? (
        <div className="mt-1">
          {SKELETON_ROWS.map((key) => (
            <TreeProjectRowSkeleton key={key} />
          ))}
        </div>
      ) : failed ? (
        <p className="body-sm mt-3 text-app-overdue">{t["selfStudy.loadError"]}</p>
      ) : isEmpty ? (
        <p className="body-sm mt-3 text-app-muted">{t["report.lessons.empty"]}</p>
      ) : (
        <div className="mt-1">
          {navigable.map((project) => (
            <TreeProjectRow
              key={project.projectId}
              name={project.name}
              imageUrl={project.imageUrl}
              caption={format(
                t[
                  project.itemCount === 1
                    ? "report.lessons.itemCountOne"
                    : "report.lessons.itemCountOther"
                ],
                { count: project.itemCount }
              )}
              onPress={() => onEnterProject(project)}
            />
          ))}

          {/* Lessons outside any folder still open as a group, named for the folder you are in. */}
          {looseLessons.length > 0 && (
            <TreeProjectRow
              name={path[path.length - 1]?.name ?? t["selfStudy.lessons.title"]}
              imageUrl={null}
              caption={lessonCount(looseLessons.length)}
              onPress={() =>
                onOpenGroup({
                  name: path[path.length - 1]?.name ?? t["selfStudy.lessons.title"],
                  lessons: looseLessons,
                })
              }
            />
          )}

          {lessonFolders.map((project) => (
            <TreeProjectRow
              key={project.projectId}
              name={project.name}
              imageUrl={project.imageUrl}
              caption={lessonCount(project.lessons.length)}
              onPress={() => onOpenGroup({ name: project.name, lessons: project.lessons })}
            />
          ))}
        </div>
      )}
    </div>
  );
}
